import { OperationVariables, TypedDocumentNode } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import { Box, Button, Stack } from "@mui/material";
import { GridRowParams, GridSortModel, GridValidRowModel } from "@mui/x-data-grid-premium";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import {
  FacilityWasteShipmentsQuery,
  FacilityWasteShipmentsQueryVariables,
  ReconciliationState,
  TenantWasteShipmentsQuery,
  TenantWasteShipmentsQueryVariables,
  WasteShipmentFieldsFragment,
  WasteShipmentStatus,
} from "generated-graphql/graphql";
import { OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { FilterKey, OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import { useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { WasteFacilityScopeBar } from "../components/WasteFacilityScopeBar";
import { WasteListPageSurface } from "../WasteListPageSurface";
import { FACILITY_WASTE_SHIPMENTS_QUERY, TENANT_WASTE_SHIPMENTS_QUERY } from "./api";
import { RECONCILIATION_LABELS } from "./reconciliationDisplay";
import { formatShipmentDate, formatTotalsByUnit, NO_VALUE_PLACEHOLDER } from "./shipmentDisplay";
import { ShipmentReconciliationChip } from "./ShipmentReconciliationChip";
import { WasteShipmentStatusChip } from "./WasteShipmentStatusChip";

type ShipmentRow = WasteShipmentFieldsFragment & {
  facility?: { id: string; name?: string | null } | null;
};

/**
 * What the reconciliation column exports and copies, matching the cell — which
 * renders nothing for NONE.
 *
 * A row with no manifest has no verdict, and exporting the words "No manifest"
 * reads as an outstanding expectation on rows that have none; canceled shipments
 * especially, since they can never take one. The placeholder is how absence is
 * spelled in every other column.
 *
 * Its own function so the branch stays out of `buildColumns`, which is long
 * enough to trip the complexity gate on one more.
 */
function reconciliationValue(status: ReconciliationState): string {
  return status === ReconciliationState.None ? NO_VALUE_PLACEHOLDER : RECONCILIATION_LABELS[status];
}

/**
 * Column fields match the backend's sortable names. Filtering runs through
 * the popover's key:value omnisearch terms (see BASE_FILTER_KEYS), so the
 * columns themselves stay filterable: false; free text still
 * contains-matches across the obvious fields.
 */
function buildColumns<TRow extends ShipmentRow>(
  includeFacility: boolean
): OmnisearchGridColDef<TRow>[] {
  const columns: OmnisearchGridColDef<TRow>[] = [
    {
      field: "manifestRequestNumber",
      headerName: "Request #",
      flex: 0.5,
      minWidth: 110,
      sortable: true,
      filterable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      minWidth: 120,
      sortable: true,
      filterable: false,
      renderCell: ({ row }) => <WasteShipmentStatusChip status={row.status} />,
    },
    {
      field: "vendor",
      headerName: "Vendor",
      flex: 0.8,
      minWidth: 140,
      sortable: true,
      filterable: false,
      valueGetter: (_, row) => row.vendor?.name ?? NO_VALUE_PLACEHOLDER,
    },
    {
      field: "containers",
      headerName: "Containers",
      flex: 0.4,
      minWidth: 100,
      sortable: false,
      filterable: false,
      valueGetter: (_, row) => row.lines.length,
    },
    {
      field: "total",
      headerName: "Total",
      flex: 0.6,
      minWidth: 120,
      sortable: false,
      filterable: false,
      valueGetter: (_, row) => formatTotalsByUnit(row.totalQuantityByUnit),
    },
    {
      field: "plannedShipDate",
      headerName: "Planned Ship",
      flex: 0.5,
      minWidth: 120,
      sortable: true,
      filterable: false,
      headerClassName: "wrapHeader",
      valueGetter: (_, row) => formatShipmentDate(row.plannedShipDate),
    },
    {
      field: "shippedDate",
      headerName: "Shipped",
      flex: 0.5,
      minWidth: 110,
      sortable: true,
      filterable: false,
      valueGetter: (_, row) => formatShipmentDate(row.shippedDate),
    },
    {
      // Hidden by default per the grid-column rollout convention.
      //
      // Not sortable and not filterable, unlike the other columns: reconciliation
      // is computed at read from the attached manifest, so there is no column for
      // the database to order or filter on. Offering either would mean sorting one
      // page of rows while claiming to sort the set.
      field: "reconciliation",
      headerName: "Reconciliation",
      flex: 0.7,
      // Fits "Differs from request" as a chip without truncating to an
      // ellipsis, which is the one thing this column exists to communicate.
      minWidth: 180,
      sortable: false,
      filterable: false,
      valueGetter: (_, row) => reconciliationValue(row.reconciliation.status),
      renderCell: ({ row }) => <ShipmentReconciliationChip status={row.reconciliation.status} />,
    },
    {
      // Hidden by default; exists so the newest-first initial sort references
      // a real column (a sort model on a nonexistent field makes the grid
      // correct its state mid-render).
      field: "createdAt",
      headerName: "Created",
      flex: 0.5,
      minWidth: 110,
      sortable: true,
      filterable: false,
      valueGetter: (_, row) => formatShipmentDate(row.createdAt),
    },
  ];
  if (includeFacility) {
    columns.splice(1, 0, {
      field: "facility",
      headerName: "Facility",
      flex: 1,
      // Facility names in this product are long ("QA Container Tracking —
      // LQG Plant"); 130px truncated every one of them to the tenant prefix,
      // which is the part they share.
      minWidth: 210,
      sortable: false,
      filterable: false,
      valueGetter: (_, row) => row.facility?.name ?? NO_VALUE_PLACEHOLDER,
    });
  }
  return columns;
}

const INITIAL_SORT: GridSortModel = [{ field: "createdAt", sort: "desc" }];
// Reconciliation ships visible. The grid-column convention is hidden-by-
// default, but this column only exists for tenants on container tracking in
// the first place, and for them whether the paperwork agrees is the reason to
// open the list — not an opt-in refinement.
const INITIAL_COLUMN_VISIBILITY = { createdAt: false };

/**
 * Popover filters write key:value omnisearch terms; the data-access layer
 * parses these into AND clauses (see getWasteShipments). The facility filter
 * is tenant-scope only — a facility-scoped grid is already pinned.
 */
const BASE_FILTER_KEYS: FilterKey[] = [
  {
    header: "Request #",
    key: "requestNumber",
    filterKeyType: "text",
  },
  {
    header: "Status",
    key: "status",
    filterKeyType: "enumAutocomplete",
    enumValues: Object.values(WasteShipmentStatus),
  },
  {
    // Tenant-scoped searchable vendor select, reusing the shared control
    // the manifests grid's vendor filter uses.
    header: "Vendor",
    key: "vendor",
    filterKeyType: "wasteVendor",
  },
  {
    header: "Transporter",
    key: "transporter",
    filterKeyType: "text",
  },
  {
    header: "Destination (TSDF)",
    key: "destination",
    filterKeyType: "text",
  },
  {
    header: "Container",
    key: "container",
    filterKeyType: "text",
  },
];
const TENANT_FILTER_KEYS: FilterKey[] = [
  ...BASE_FILTER_KEYS,
  {
    header: "Facility",
    key: "facilityId",
    filterKeyType: "wasteMultipleFacility",
  },
];
const BASE_FILTER_GROUPS: FilterGroup[] = [
  {
    name: "Shipment",
    fields: ["requestNumber", "status", "vendor", "transporter", "destination", "container"],
  },
];
const TENANT_FILTER_GROUPS: FilterGroup[] = [
  {
    name: "Shipment",
    fields: [
      "requestNumber",
      "status",
      "vendor",
      "transporter",
      "destination",
      "container",
      "facilityId",
    ],
  },
];

const getTenantItems = (data: TenantWasteShipmentsQuery) =>
  data.tenant?.wasteShipments?.items ?? [];
const getTenantCount = (data: TenantWasteShipmentsQuery) => data.tenant?.wasteShipments?.count ?? 0;
const getFacilityItems = (data: FacilityWasteShipmentsQuery) =>
  data.facility?.wasteShipments?.items ?? [];
const getFacilityCount = (data: FacilityWasteShipmentsQuery) =>
  data.facility?.wasteShipments?.count ?? 0;

const toApiSortModel = (sort: GridSortModel) => sort.map((s) => ({ field: s.field, sort: s.sort }));

/**
 * The shipments list rendered for either scope; mirrors the containers grid
 * split — shared grid settings live here once, the scopes differ only in
 * query/getters/persistence key/facility column.
 */
function ShipmentsGrid<
  TData,
  TVariables extends OperationVariables,
  TRow extends ShipmentRow & GridValidRowModel,
>({
  tenantId,
  persistenceKey,
  query,
  getItems,
  getCount,
  buildVariables,
  noRowsMessage,
  includeFacility,
  onRowClick,
  commandButtons,
  filterGroups,
  additionalFilterKeys,
}: {
  tenantId: string;
  persistenceKey: string;
  query: TypedDocumentNode<TData, TVariables>;
  getItems: (data: TData) => TRow[];
  getCount: (data: TData) => number;
  buildVariables: (
    omnisearch: string,
    pagination: { page: number; pageSize: number },
    sort: GridSortModel
  ) => TVariables;
  noRowsMessage: string;
  includeFacility: boolean;
  onRowClick: (params: GridRowParams<ShipmentRow>) => void;
  commandButtons: React.ReactNode[];
  filterGroups: FilterGroup[];
  additionalFilterKeys: FilterKey[];
}) {
  const columns = useMemo(() => buildColumns<TRow>(includeFacility), [includeFacility]);
  return (
    <OmnisearchDataGridV2
      persistenceKey={persistenceKey}
      columns={columns}
      filterGroups={filterGroups}
      additionalFilterKeys={additionalFilterKeys}
      query={query}
      getItems={getItems}
      getCount={getCount}
      buildVariables={buildVariables}
      noRowsMessage={noRowsMessage}
      initialSortModel={INITIAL_SORT}
      initialColumnVisibilityModel={INITIAL_COLUMN_VISIBILITY}
      onRowClick={onRowClick}
      disableRowSelectionOnClick
      commandButtons={commandButtons}
      tenantId={tenantId}
    />
  );
}

function TenantShipmentsGrid({
  tenantId,
  onRowClick,
  commandButtons,
}: {
  tenantId: string;
  onRowClick: (params: GridRowParams<ShipmentRow>) => void;
  commandButtons: React.ReactNode[];
}) {
  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): TenantWasteShipmentsQueryVariables => ({
      tenantId,
      search: omnisearch,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: toApiSortModel(sort),
    }),
    [tenantId]
  );
  return (
    <ShipmentsGrid<
      TenantWasteShipmentsQuery,
      TenantWasteShipmentsQueryVariables,
      NonNullable<
        NonNullable<TenantWasteShipmentsQuery["tenant"]>["wasteShipments"]
      >["items"][number]
    >
      tenantId={tenantId}
      persistenceKey="tenant-waste-shipments-table"
      query={TENANT_WASTE_SHIPMENTS_QUERY}
      getItems={getTenantItems}
      getCount={getTenantCount}
      buildVariables={buildVariables}
      noRowsMessage="No shipments have been packaged for this organization."
      includeFacility
      onRowClick={onRowClick}
      commandButtons={commandButtons}
      filterGroups={TENANT_FILTER_GROUPS}
      additionalFilterKeys={TENANT_FILTER_KEYS}
    />
  );
}

function FacilityShipmentsGrid({
  tenantId,
  facilityId,
  onRowClick,
  commandButtons,
}: {
  tenantId: string;
  facilityId: string;
  onRowClick: (params: GridRowParams<ShipmentRow>) => void;
  commandButtons: React.ReactNode[];
}) {
  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): FacilityWasteShipmentsQueryVariables => ({
      facilityId,
      search: omnisearch,
      page: pagination.page,
      pageSize: pagination.pageSize,
      sort: toApiSortModel(sort),
    }),
    [facilityId]
  );
  return (
    <ShipmentsGrid<
      FacilityWasteShipmentsQuery,
      FacilityWasteShipmentsQueryVariables,
      WasteShipmentFieldsFragment
    >
      tenantId={tenantId}
      persistenceKey="facility-waste-shipments-table"
      query={FACILITY_WASTE_SHIPMENTS_QUERY}
      getItems={getFacilityItems}
      getCount={getFacilityCount}
      buildVariables={buildVariables}
      noRowsMessage="No shipments have been packaged at this facility."
      includeFacility={false}
      onRowClick={onRowClick}
      commandButtons={commandButtons}
      filterGroups={BASE_FILTER_GROUPS}
      additionalFilterKeys={BASE_FILTER_KEYS}
    />
  );
}

export function WasteShipments() {
  const { tenantId } = useTenant();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const facilityId = searchParams.get("facilityId");

  useBreadcrumb([
    { label: "Waste", to: tenantId ? `/o/${tenantId}/waste` : undefined },
    { label: "Shipments" },
  ]);

  const changeFacilityScope = (nextFacilityId: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (nextFacilityId) {
      next.set("facilityId", nextFacilityId);
    } else {
      next.delete("facilityId");
    }
    setSearchParams(next, { replace: true });
  };

  const handleRowClick = useCallback(
    (params: GridRowParams<ShipmentRow>) => {
      navigate(`/o/${tenantId}/waste/shipments/${params.row.id}`);
    },
    [navigate, tenantId]
  );

  const commandButtons = useMemo(
    () => [
      <Button
        key="package-shipment"
        variant="contained"
        size="small"
        startIcon={<Add />}
        onClick={() =>
          navigate(
            facilityId
              ? `/o/${tenantId}/waste/shipments/new?facilityId=${facilityId}`
              : `/o/${tenantId}/waste/shipments/new`
          )
        }
      >
        Package a shipment
      </Button>,
    ],
    [facilityId, navigate, tenantId]
  );

  if (!tenantId) {
    return null;
  }

  return (
    <>
      {/* Scope is chosen here rather than only inherited from wherever the
          reader arrived: the list could be scoped by a link and unscoped by a
          chip, but there was no way to scope it from the page itself. The same
          bar the container list and the packaging page draw, so moving between
          the three is one control in one place. */}
      <WasteFacilityScopeBar facilityId={facilityId} onChangeFacility={changeFacilityScope} />
      <WasteListPageSurface>
        <Stack spacing={3}>
          <Box>
            {/* Scoped, the Facility column goes: every row would repeat the
                value the bar above already states. */}
            {facilityId ? (
              <FacilityShipmentsGrid
                tenantId={tenantId}
                facilityId={facilityId}
                onRowClick={handleRowClick}
                commandButtons={commandButtons}
              />
            ) : (
              <TenantShipmentsGrid
                tenantId={tenantId}
                onRowClick={handleRowClick}
                commandButtons={commandButtons}
              />
            )}
          </Box>
        </Stack>
      </WasteListPageSurface>
    </>
  );
}
